/* ==========================================================================
   iBuddies Recipe Hub — recipes.js
   Hydrates server-rendered cards · search · filters · scaler · switcher · share

   DELIVERY MODEL (Kelvin's ruling 2026-08-26)
     繁中 + English are server-rendered into the HTML by build_page.py and are
     present with JavaScript off. Indonesian and Filipino are fetched on demand
     from recipes-i18n.json the first time a reader asks for one, because
     ID/FIL have no search market for these dishes in Hong Kong — the helper
     arrives through the employer's WhatsApp share, not through Google.

   So THIS FILE NEVER BUILDS A CARD. build_page.py owns card markup; this file
   binds behaviour to it and injects the two on-demand languages. One producer.

   Contract with the host page (page.recipes.liquid or preview.html):
     <div id="recipe-hub" class="rh" data-lang="zh" data-prerendered="zh,en">
       <header class="rh-head"> …hero: kicker, <h1>, sub — from recipe-hero.liquid… </header>
       <div class="rh-grid"> …articles from the snippets… </div>
     </div>
   The <header> is server-rendered too, so a JS-off reader gets a page-level heading
   instead of 25 <h2>s. This file only mounts the language buttons into it, and
   inserts its own controls AFTER it.
     <script>window.RECIPE_HUB = {
        taxonomyUrl: "…/taxonomy.json",
        i18nUrl:     "…/recipes-i18n.json",
        productBase: "https://slibuddies.net/products/",
        pageUrl:     "https://slibuddies.net/pages/recipes",
        lang:        "zh"
     };</script>

   Hard rules encoded here (each is in PLAN.md because it already broke):
     - allergenNote / serveNote / familyShare are banners, never steps, never in
       JSON-LD. familyShare (一盤兩食) IS carried in the WhatsApp share body: the
       helper cooks from that message, not from the blog post.
     - blogUrl is the exact MIRROR of that rule: the card links to the blog
       post, and shareText() must NOT. The share already carries 📖 the hub and
       🛒 the ordering link; a third destination sends the helper out of the
       message she is cooking from. Do not add `lines.push('📖 ' + r.blogUrl)`
       below — _Tools/recipe-hub-deploy/test_share_body.js checks 13-16 drive
       this function with a blogUrl present and fail if it appears.
     - credit is SPLIT between those two rules and deliberately so: the credit
       TEXT is in the share body (an attribution is part of the recipe, and a
       recipe pasted onward without it is the recipe with the credit stripped),
       the credit URL is not (it is the third destination blogUrl forbids). It
       is never a step: build_page.py asserts it stays out of JSON-LD
       recipeInstructions, per language.
     - allergen chips come from allergens[]; dietary chips from badges[].
     - total time is totalMin (data-total), never prep + cook.
     - non-linear scaling comes from the DATA (linearScale -> data-linear),
       not from a SKU list in a renderer. 10th amendment, 2026-08-26.
     - the switcher never shows an empty card: ID/FIL only becomes the display
       language once its text is in the DOM, and a recipe the payload could not
       hydrate falls back to marked Chinese rather than rendering blank.
     - the WhatsApp share body carries the BRANDED /wa/ ordering link from
       data-order-link, never a /products/ URL and never a constructed slug.
     - a card with NO product (data-no-product, BABY-IMPORT-PLAN ruling 2) still
       shares — it just has no 🛒 line, no CTA and no buy chip. A card with no
       link and no declaration does NOT share: that is a stale snippet, and the
       two cases are told apart by the declaration, never by the empty value.
     - identity is data-rid (the recipe `id`), on every card. data-sku is a
       PRODUCT reference that several cards may share, and is never identity.
   ========================================================================== */
(function () {
  'use strict';

  var VERSION = '2.7.1';   /* 2.7.1 = lamb + chef labels English by ruling; allergen gap scan, 2026-09-10
                              2.7.0 = chef-inspired tag + rail toggle, 2026-09-10
                              2.6.0 = credit in the share body + `egg` category, 2026-09-08
                              2.5.0 = declared pendingLangs -> marked English, 2026-08-31 */
  var LANGS = ['zh', 'en', 'id', 'fil'];
  var SERVER_LANGS = ['zh', 'en'];
  var CLIENT_LANGS = ['id', 'fil'];
  var LANG_LABEL = { zh: '繁中', en: 'EN', id: 'ID', fil: 'FIL' };
  var LANG_TAG = { zh: 'zh-Hant', en: 'en', id: 'id', fil: 'fil' };

  var CFG = window.RECIPE_HUB || {};
  var PRODUCT_BASE = CFG.productBase || 'https://slibuddies.net/products/';
  var PAGE_URL = CFG.pageUrl || 'https://slibuddies.net/pages/recipes';
  var STORE_KEY = 'ibuddies-recipe-lang';

  /* ---------------------------------------------------------------- strings
     Interactive chrome only. Everything that is server-rendered (section
     headings, time labels, buy label, banner headings) lives in build_page.py
     so no string has two homes. ID/FIL here are developer drafts — README
     flags them for ibuddies-translator.
  ------------------------------------------------------------------------- */
  /* The hero (kicker / <h1> / sub) is SERVER-RENDERED by build_page.py and is no
     longer built here — with JavaScript off the page must still have a page-level
     heading, not 25 <h2>s. Its strings live only in build_page.py's HERO. */
  var T = {
    search:     { zh: '搜尋食譜或食材，例如：排骨、牛肉、湯', en: 'Search a dish or an ingredient, e.g. ribs, beef, soup', id: 'Cari masakan atau bahan, misalnya iga, sapi, sup', fil: 'Maghanap ng ulam o sangkap, hal. tadyang, baka, sabaw' },
    catRow:     { zh: '分類', en: 'Category', id: 'Kategori', fil: 'Kategorya' },
    timeRow:    { zh: '時間', en: 'Time', id: 'Waktu', fil: 'Oras' },
    dietRow:    { zh: '飲食', en: 'Diet', id: 'Diet', fil: 'Diyeta' },
    /* The age row. Only rendered when the page carries at least one card with an
       age band, so an adult-only hub never grows a baby control. */
    ageRow:     { zh: '寶寶年齡', en: 'Baby age', id: 'Usia bayi', fil: 'Edad ng sanggol' },
    all:        { zh: '全部', en: 'All', id: 'Semua', fil: 'Lahat' },
    resultsOne: { zh: '個食譜', en: 'recipe', id: 'resep', fil: 'resipe' },
    resultsMany:{ zh: '個食譜', en: 'recipes', id: 'resep', fil: 'mga resipe' },
    empty:      { zh: '搵唔到相關食譜，試下另一個食材名。', en: 'No recipes match. Try another ingredient name.', id: 'Tidak ada resep yang cocok. Coba nama bahan lain.', fil: 'Walang tumugmang resipe. Subukan ang ibang pangalan ng sangkap.' },
    close:      { zh: '收起', en: 'Close', id: 'Tutup', fil: 'Isara' },
    servings:   { zh: '份量', en: 'Servings', id: 'Porsi', fil: 'Serving' },
    share:      { zh: '分享俾幫手', en: 'Share to helper', id: 'Bagikan ke asisten', fil: 'Ibahagi sa kasambahay' },
    sharePick:  { zh: '揀語言組合。分享出去嘅訊息會同時有中文同埋對方嘅語言。',
                  en: 'Pick a pairing. The shared message carries Chinese plus the reader’s language, side by side.',
                  id: 'Pilih pasangan bahasa. Pesan yang dibagikan berisi bahasa Mandarin dan bahasa Anda sekaligus.',
                  fil: 'Pumili ng pares ng wika. Ang mensaheng ibabahagi ay may Chinese at ang wika mo, magkatabi.' },
    scaleNote:  { zh: '份量已按新人數調整。留意：步驟文字入面嘅數字冇改，請以上面嘅食材份量為準。',
                  en: 'Ingredient amounts are scaled. Note: the numbers written inside the steps are unchanged, so follow the ingredient list above.',
                  id: 'Jumlah bahan sudah disesuaikan. Catatan: angka di dalam langkah-langkah tidak berubah, jadi ikuti daftar bahan di atas.',
                  fil: 'Na-adjust na ang dami ng sangkap. Paalala: hindi nagbago ang mga bilang sa loob ng mga hakbang, kaya sundin ang listahan ng sangkap sa itaas.' },
    gap:        { zh: '⚠️ 部分介面標籤未有印尼文／菲律賓文翻譯，標示 * 嘅暫時顯示英文。',
                  en: '⚠️ Some interface labels have no Indonesian or Filipino translation yet. Anything marked * is shown in English.',
                  id: '⚠️ Sebagian label antarmuka belum diterjemahkan ke bahasa Indonesia. Yang bertanda * masih dalam bahasa Inggris.',
                  fil: '⚠️ May ilang label sa interface na wala pang salin sa Filipino. Ang may * ay nasa Ingles pa rin.' },
    footer:     { zh: '份量、時間同敏感資料由 iBuddies 團隊編寫。落單或者查詢，請用網站上嘅 WhatsApp 連結。',
                  en: 'Quantities, timings and allergen data are written by the iBuddies team. To order or ask, use the WhatsApp link on the site.',
                  id: 'Jumlah, waktu, dan data alergen ditulis oleh tim iBuddies. Untuk memesan atau bertanya, gunakan tautan WhatsApp di situs.',
                  fil: 'Ang dami, oras at datos ng allergen ay isinulat ng team ng iBuddies. Para umorder o magtanong, gamitin ang WhatsApp link sa site.' },
    /* on-demand language loading — the reader must never see an empty card */
    i18nLoading: { zh: '正在載入印尼文／菲律賓文…', en: 'Loading Indonesian / Filipino…',
                   id: 'Memuat bahasa Indonesia…', fil: 'Naglo-load ng Filipino…' },
    i18nFail:    { zh: '⚠️ 載入唔到印尼文／菲律賓文，暫時維持原本語言。請撳「再試一次」。',
                   en: '⚠️ Could not load Indonesian / Filipino, so the page stayed in the language you were reading. Try again.',
                   id: '⚠️ Teks bahasa Indonesia gagal dimuat, jadi halaman tetap dalam bahasa sebelumnya. Coba lagi.',
                   fil: '⚠️ Hindi na-load ang tekstong Filipino, kaya nanatili ang dating wika ng pahina. Subukan ulit.' },
    /* a PARTIAL payload: the language switched, but some cards have no text in it.
       {n} is substituted per language by tSpansN(). */
    i18nPartial: { zh: '⚠️ 有 {n} 個食譜未有呢個語言嘅內容。嗰幾張卡會照顯示中文，卡上面有標示。',
                   en: '⚠️ {n} recipe(s) have no text in this language yet. Those cards stay in Chinese and say so on the card.',
                   id: '⚠️ {n} resep belum punya teks dalam bahasa ini. Kartu-kartu itu tetap berbahasa Mandarin dan diberi keterangan.',
                   fil: '⚠️ {n} resipe ang wala pang teksto sa wikang ito. Nananatiling Chinese ang mga kard na iyon at may paalala sa kanila.' },
    /* the same failure, said ON the card that has it — a reader who never scrolls
       past the banner still cannot mistake Chinese text for their own language */
    langMissing: { zh: '⚠️ 呢個食譜未有印尼文／菲律賓文版本，以下內容維持中文。',
                   en: '⚠️ This recipe has no Indonesian / Filipino text yet, so everything below stays in Chinese.',
                   id: '⚠️ Resep ini belum ada versi bahasa Indonesianya, jadi semua di bawah tetap dalam bahasa Mandarin. Mohon minta bantuan untuk membacanya.',
                   fil: '⚠️ Wala pang Filipino na bersyon ang resiping ito, kaya Chinese ang lahat sa ibaba. Pakisuyong ipabasa ito sa iba.' },
    /* A DECLARED gap, not a failure. Different words from langMissing because the
       reader gets ENGLISH here, not Chinese, and because "not translated yet" is a
       true and finite statement where "something went wrong" would not be. */
    langPending: { zh: '⚠️ 呢個食譜嘅印尼文／菲律賓文版本仲未做起，以下內容暫時顯示英文。',
                   en: '⚠️ The Indonesian / Filipino version of this recipe is not written yet, so everything below is shown in English for now.',
                   id: '⚠️ Versi bahasa Indonesia resep ini belum dibuat, jadi semua di bawah ditampilkan dalam bahasa Inggris untuk sementara.',
                   fil: '⚠️ Hindi pa nagagawa ang Filipino na bersyon ng resiping ito, kaya Ingles muna ang lahat sa ibaba.' },
    /* why the 中+ID / 中+FIL share is switched off on such a card */
    shareBlocked: { zh: '呢個食譜未有印尼文／菲律賓文，所以暫時只可以分享中文＋英文版本。',
                    en: 'This recipe has no Indonesian / Filipino text, so only the Chinese + English share is available.',
                    id: 'Resep ini belum ada teks bahasa Indonesianya, jadi hanya bagikan Mandarin + Inggris yang tersedia.',
                    fil: 'Walang Indonesian / Filipino na teksto ang resiping ito, kaya Chinese + English lang ang puwedeng ibahagi.' },
    retry:       { zh: '再試一次', en: 'Try again', id: 'Coba lagi', fil: 'Subukan ulit' }
  };

  /* servesText is mirrored in build_page.py — change both or the scaler and the
     card chip disagree on the same card. */
  /* Mirrored in build_page.py BATCH_BAKES -- these count pieces or family shares, not
     baby meals, so they keep the neutral label until Kelvin rules. */
  var BATCH_BAKES = {
    'baby-banana-oat-cookies': 1, 'baby-banana-cupcakes': 1, 'baby-banana-waffles': 1,
    'baby-croissant': 1, 'baby-autolyse-bread': 1, 'baby-wholewheat-bread': 1,
    'baby-greek-yogurt-bread': 1, 'baby-chicken-corn-muffins': 1, 'baby-simple-cake': 1
  };
  function isBabyMeal(r) { return !!r.age && !BATCH_BAKES[r.rid]; }
  function isPieces(r) { return !!r.age && !!BATCH_BAKES[r.rid]; }

  function servesText(n, lang, baby, pieces) {
    /* A batch bake yields PIECES -- its own note says "8-10 small cookies". Founder
       ruling 2026-09-02. */
    if (pieces) {
      if (lang === 'zh') return n + ' 件';
      if (lang === 'en') return n + (n === 1 ? ' piece' : ' pieces');
      if (lang === 'id') return n + ' potong';
      return n + ' piraso';
    }
    /* A BABY card counts baby MEALS, not people. '2 人份' and 'para sa 2 na tao' both
       literally say two people, which on a baby recipe reads as a dish for two adults
       when it means one baby, two meals. Founder ruling 2026-09-01. */
    if (baby) {
      if (lang === 'zh') return n + ' 份寶寶餐';
      if (lang === 'en') return n + (n === 1 ? ' baby meal' : ' baby meals');
      if (lang === 'id') return n + ' porsi bayi';
      return n + ' pagkain ng sanggol';
    }
    if (lang === 'zh') return n + ' 人份';
    if (lang === 'en') return n + (n === 1 ? ' serving' : ' servings');
    if (lang === 'id') return n + ' porsi';
    return 'para sa ' + n + (n === 1 ? ' tao' : ' na tao');
  }

  var FALLBACK_CATS = [
    { key: 'pork', zh: '豬肉', en: 'Pork', id: 'Babi', fil: 'Baboy' },
    { key: 'beef', zh: '牛肉', en: 'Beef', id: 'Sapi', fil: 'Baka' },
    /* lamb — id/fil are the ENGLISH word by founder ruling (Kelvin 2026-09-10), not a
       gap: neither language has an unambiguous term (ID kambing=goat/domba=sheep, FIL
       tupa=animal/kordero=meat). Real values, so no * marker. See build_data.py. */
    { key: 'lamb', zh: '羊肉', en: 'Lamb', id: 'Lamb', fil: 'Lamb' },
    { key: 'poultry', zh: '雞', en: 'Chicken', id: 'Ayam', fil: 'Manok' },
    /* egg, 2026-09-08. id/fil null on purpose — no translator proposal exists yet, and
       taxonomy.json's nullPolicy prefers a visible gap to an invented label. */
    { key: 'egg', zh: '蛋類', en: 'Eggs', id: null, fil: null },
    { key: 'seafood', zh: '海鮮', en: 'Seafood', id: 'Makanan Laut', fil: 'Pagkaing-dagat' },
    { key: 'driedseafood', zh: '海味', en: 'Dried Seafood', id: 'Makanan Laut Kering', fil: 'Tuyong Pagkaing-dagat' },
    { key: 'soup', zh: '湯水', en: 'Soup', id: 'Sup', fil: 'Sabaw' },
    { key: 'baby', zh: '寶寶', en: 'Baby', id: 'Bayi', fil: 'Sanggol' }
  ];
  /* Taxonomy can fail to load; the pill must still be able to render with a real label
     rather than disappearing, which would look identical to "no chef recipes on this
     page". Same null-on-purpose rule as `egg` above — see CHEF_PICK in build_data.py. */
  var FALLBACK_CHEF = { key: 'chefpick', zh: '名廚啟發', en: 'Chef-Inspired', id: 'Chef-Inspired', fil: 'Chef-Inspired' };
  var FALLBACK_SERVINGS = [
    { mult: 1, zh: '原本份量', en: 'As written', id: null, fil: null },
    { mult: 2, zh: '雙倍', en: 'Double', id: null, fil: null },
    { mult: 3, zh: '三倍', en: 'Triple', id: null, fil: null }
  ];
  var FREE_LABEL = {
    pork:    { zh: '無豬肉', en: 'Pork-free', id: 'Tanpa babi', fil: 'Walang baboy' },
    alcohol: { zh: '無酒精', en: 'Alcohol-free', id: 'Tanpa alkohol', fil: 'Walang alkohol' },
    beef:    { zh: '無牛肉', en: 'Beef-free', id: 'Tanpa daging sapi', fil: 'Walang baka' }
  };
  /* ------------------------------------------------------------------------
     AGE BANDS — a safety filter, not a tag row.

     SEMANTICS, and they are deliberately asymmetric: picking an age shows every
     recipe suitable AT OR BELOW it. A parent of a 9-month-old may cook the 6m,
     7m, 8m and 9m recipes; they may NOT cook the 12m one. So the filter widens
     downward and never upward, because the failure modes are not symmetrical —
     hiding a recipe a baby could have eaten is an inconvenience, showing one a
     baby should not eat is the thing this field exists to prevent.

     And picking an age hides every recipe with NO band at all (i.e. every adult
     recipe). "Suitable for a 9-month-old" is a claim we have made about the baby
     recipes and about nothing else; a 蒜蓉蒸排骨 appearing under that heading
     would be a claim nobody wrote.

     The labels are the same wording as the chip build_page.py bakes onto the card.
  ------------------------------------------------------------------------- */
  var AGE_BANDS = ['6m', '7m', '8m', '9m', '10m', '11m', '12m'];
  function ageLabel(band) {
    var n = band.replace('m', '');
    return { key: band, zh: n + ' 個月', en: n + ' months', id: n + ' bulan', fil: n + ' buwan' };
  }
  function ageMonths(band) { return parseInt(band, 10) || 0; }

  var TIME_BUCKETS = [
    { key: 't30', max: 30, zh: '30 分鐘內', en: 'Under 30 min', id: 'Di bawah 30 menit', fil: 'Wala pang 30 minuto' },
    { key: 't60', max: 60, zh: '60 分鐘內', en: 'Under 60 min', id: 'Di bawah 60 menit', fil: 'Wala pang 60 minuto' },
    { key: 't99', max: Infinity, zh: '60 分鐘以上', en: 'Over 60 min', id: 'Di atas 60 menit', fil: 'Mahigit 60 minuto' }
  ];

  /* ------------------------------------------------------------------------
     Non-linear scaling.
     WHICH recipes is data (linearScale -> data-linear="0"). WHAT to say is UI
     copy, and stays here. A SKU with no specific copy still gets the generic
     warning, so a future non-linear recipe cannot ship unwarned.
  ------------------------------------------------------------------------- */
  var SCALE_WARN_GENERIC = {
    zh: '⚠️ 呢個食譜嘅時間唔係按人數計。加份量之後，請分批煮，唔好一次過逼晒落鑊，時間亦要自己睇住食材嘅狀態調整。',
    en: '⚠️ The timings in this recipe do not scale with the servings. Cook in batches, do not crowd the pan, and judge doneness by the food rather than the clock.',
    id: '⚠️ Waktu dalam resep ini tidak ikut bertambah bersama porsi. Masak bertahap, jangan penuhi wajan, dan nilai kematangan dari keadaan bahannya, bukan dari jam.',
    fil: '⚠️ Ang mga oras sa resipeng ito ay hindi dumadami kasabay ng serving. Lutuin nang hindi sabay-sabay, huwag punuin ang kawali, at tingnan ang hitsura ng pagkain para malaman kung luto na — hindi ang orasan.'
  };
  var SCALE_WARN_BY_SKU = {
    AMABE00026: {
      zh: '⚠️ 呢個食譜嘅煎製時間係「每件牛扒」計，唔係按人數計。加人數即係加牛扒：一隻鑊一次煎一件，分批煎，唔好逼滿隻鑊，否則塊牛扒會出水變焗，煎唔到脆邊。食材份量已經按人數調整，但每件牛扒嘅煎製時間同火路維持不變。',
      en: '⚠️ The sear timings in this recipe are per steak, not per portion. More servings means more steaks: sear one steak at a time, in batches. Do not crowd the pan, or the meat releases water and steams instead of browning. Ingredient amounts are scaled, but the sear time and the heat stay the same for each steak.',
      id: '⚠️ Waktu menggoreng dalam resep ini dihitung per potong steak, bukan per porsi. Porsi lebih banyak berarti steak lebih banyak: goreng satu steak dalam satu wajan, satu per satu, bertahap. Jangan penuhi wajan, karena daging akan mengeluarkan air lalu terkukus dan tidak kecokelatan. Jumlah bahan sudah disesuaikan, tetapi waktu dan besar api untuk setiap potong steak tetap sama.',
      fil: '⚠️ Ang oras ng pagpiprito sa resipeng ito ay bawat isang steak, hindi bawat serving. Mas maraming serving, mas maraming steak: isa-isang iprito ang steak sa kawali, hindi sabay-sabay. Huwag punuin ang kawali dahil lalabas ang tubig ng karne at masisingawan ito sa halip na mag-brown. Na-adjust na ang dami ng sangkap, pero pareho pa rin ang oras at lakas ng apoy sa bawat steak.'
    }
  };
  function scaleWarnFor(sku) { return SCALE_WARN_BY_SKU[sku] || SCALE_WARN_GENERIC; }

  /* ============================================================== utilities */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function norm(s) {
    var t = String(s == null ? '' : s).toLowerCase();
    return t.normalize ? t.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : t;
  }
  var MISSING_TIP = {
    zh: '未有翻譯，暫時顯示英文', en: 'translation missing, showing English',
    id: 'terjemahan belum ada, ditampilkan dalam bahasa Inggris',
    fil: 'wala pang salin, Ingles ang ipinapakita'
  };
  /* mirrors lang_spans() in build_page.py, for the interactive chrome only */
  function langSpans(obj, cls) {
    var out = '';
    for (var i = 0; i < LANGS.length; i++) {
      var l = LANGS[i];
      var raw = obj ? obj[l] : null;
      var missing = (raw === null || raw === undefined || raw === '');
      var v = missing ? ((obj && obj.en) || '') : raw;
      var klass = (cls || '') + (missing ? ' rh-untranslated' : '');
      out += '<span data-l="' + l + '"' + (klass.trim() ? ' class="' + klass.trim() + '"' : '') +
             (missing ? ' title="' + esc(MISSING_TIP[l]) + '" lang="en"' : '') + '>' + esc(v) +
             (missing ? '<sup aria-hidden="true">*</sup>' : '') + '</span>';
    }
    return out;
  }
  function tSpans(key, cls) { return langSpans(T[key], cls); }
  /* tSpans with a {n} placeholder filled in every language */
  function tSpansN(key, cls, n) {
    var o = {};
    for (var i = 0; i < LANGS.length; i++) {
      o[LANGS[i]] = String(T[key][LANGS[i]]).replace('{n}', String(n));
    }
    return langSpans(o, cls);
  }

  /* ============================================================ scaleQty()
     Rescales a quantity STRING in place, per language, keeping every word.
       - numbers, decimals (ID comma / ZH-EN-FIL period), fractions, 半/half
       - ranges scale as ranges, because every number in the string scales
       - count units round to the nearest 0.5, so nobody gets 2.33 eggs
       - non-numeric quantities (少許 / to taste) pass through
       - factor 1 returns the original string untouched
  ------------------------------------------------------------------------- */
  var COUNT_UNITS = {};
  (function () {
    var list = (
      '個 隻 片 條 粒 瓣 棵 朵 枝 件 包 罐 紮 角 塊 尾 碗 支 束 打 ' +
      'piece pieces pcs pc pack packs can cans clove cloves slice slices ' +
      'stalk stalks sprig sprigs bundle bundles floret florets head heads ' +
      'egg eggs prawn prawns bowl bowls chilli chillies chili onion onions ' +
      'pepper peppers shallot shallots radish whole fillet fillets steak steaks ' +
      'buah butir siung iris batang lembar ekor potong bungkus kaleng ikat ' +
      'kuntum tangkai mangkuk keping ' +
      'piraso hiwa pakete lata bigkis ulo buo tangkay tasa mangkok'
    ).split(/\s+/);
    for (var i = 0; i < list.length; i++) if (list[i]) COUNT_UNITS[list[i]] = true;
  })();

  var HALF_RE = /半|\bhalf\b|\bsetengah\b|\bkalahati\b/i;
  var TOKEN_RE = /(\d+\s*\/\s*\d+)|(\d+(?:[.,]\d+)?)|(半)|(\bhalf\b)|(\bsetengah\b)|(\bkalahati\b)/gi;

  function unitAfter(str, idx) {
    var rest = str.slice(idx).replace(/^[\s　（(]*/, '');
    var cjk = rest.match(/^[一-鿿]{1,2}/);
    if (cjk) {
      if (COUNT_UNITS[cjk[0]]) return cjk[0];
      if (COUNT_UNITS[cjk[0].charAt(0)]) return cjk[0].charAt(0);
      return cjk[0];
    }
    var lat = rest.match(/^[A-Za-zÀ-ÿ]+/);
    return lat ? lat[0].toLowerCase() : '';
  }
  function isCountUnit(u) {
    if (!u) return true;                       /* bare "2" = a count of the named item */
    if (COUNT_UNITS[u]) return true;
    return !!COUNT_UNITS[u.replace(/s$/, '')];
  }
  function roundCount(v) { var r = Math.round(v * 2) / 2; return r < 0.5 ? 0.5 : r; }
  function roundMeasure(v) {
    if (v < 10) return Math.round(v * 4) / 4;
    if (v < 100) return Math.round(v);
    return Math.round(v / 5) * 5;
  }
  function fmtNum(v, lang, style) {
    if (Math.abs(v - Math.round(v)) < 1e-9) return String(Math.round(v));
    var whole = Math.floor(v);
    var rem = Math.round((v - whole) * 100) / 100;
    var FRAC = { 0.25: '1/4', 0.5: '1/2', 0.75: '3/4' };
    if (style === 'fraction' && FRAC[rem]) {
      /* zh writes 半茶匙 for a bare half, but "1 1/2個" is not Chinese —
         anything above 1 falls through to the decimal form (1.5個). */
      if (lang === 'zh') {
        if (whole === 0 && rem === 0.5) return '半';
      } else {
        return whole ? whole + ' ' + FRAC[rem] : FRAC[rem];
      }
    }
    var s = String(Math.round(v * 100) / 100);
    return lang === 'id' ? s.replace('.', ',') : s;
  }

  function scaleQty(qty, factor, lang) {
    if (!qty || factor === 1) return qty;
    if (!/\d/.test(qty) && !HALF_RE.test(qty)) return qty;
    TOKEN_RE.lastIndex = 0;
    return qty.replace(TOKEN_RE, function (m, frac, num, zhHalf, enHalf, idHalf, filHalf, offset, whole) {
      var base, style;
      if (frac) {
        var p = frac.split('/');
        base = parseFloat(p[0]) / parseFloat(p[1]);
        style = 'fraction';
      } else if (num) {
        base = parseFloat(num.replace(',', '.'));
        style = /[.,]/.test(num) ? 'decimal' : 'integer';
      } else {
        base = 0.5;
        style = 'fraction';
      }
      if (!isFinite(base)) return m;
      var unit = unitAfter(whole, offset + m.length);
      var val = base * factor;
      val = isCountUnit(unit) ? roundCount(val) : roundMeasure(val);
      return fmtNum(val, lang, style === 'integer' ? 'decimal' : style);
    });
  }

  /* ============================================================== tracking */
  function track(action, detail) {
    var payload = { event: 'recipe_hub_' + action, hub_version: VERSION };
    for (var k in detail) if (Object.prototype.hasOwnProperty.call(detail, k)) payload[k] = detail[k];
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('recipe-hub:' + action, { detail: payload, bubbles: true }));
    } catch (e) {}
  }

  /* ================================================================ state */
  var state = {
    recipes: [],
    cats: FALLBACK_CATS,
    servings: FALLBACK_SERVINGS,
    gapLangs: {},
    lang: 'zh',
    q: '',
    cat: 'all',
    time: 'all',
    age: 'all',
    hasAges: false,     /* does this page carry any age-banded card at all? */
    /* The chef-inspired axis. A BOOLEAN TOGGLE, not a value in state.cat, and the two
       combine: 豬肉 + 名廚啟發 narrows to the chef-inspired pork. Folding it into the
       category rail's single-select would have made "chef" mean "forget the category you
       picked", which is the one thing a reader who just picked a category does not want. */
    chef: false,
    hasChef: false,     /* does this page carry any chef-inspired card at all? */
    chefLabel: null,    /* from taxonomy CHEF_PICK; null id/fil render the * gap */
    free: {},
    scale: {},          /* scale[rid] = multiplier */
    i18n: null,         /* the fetched ID/FIL payload */
    i18nState: 'none',  /* none | loading | ready | failed */
    /* SKUs the payload could not hydrate (absent, or an ing/step count mismatch).
       Those cards fall back to Chinese and are marked; they are NOT left blank, and
       their 中+ID / 中+FIL share is switched off. See injectI18n(). */
    i18nDegraded: []
  };
  var root, gridEl, headEl, countEl, emptyEl, statusEl;

  /* ============================================================ data load */
  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error(r.status + ' ' + url);
      return r.json();
    });
  }
  function coerceTaxonomy(j) {
    var out = { cats: FALLBACK_CATS, servings: FALLBACK_SERVINGS, chefLabel: FALLBACK_CHEF };
    if (!j) return out;
    var cats = j.CATEGORIES || j.categories;
    if (Array.isArray(cats) && cats.length) {
      out.cats = cats.map(function (o) {
        var c = { key: o.key || o.cat };
        for (var i = 0; i < LANGS.length; i++) c[LANGS[i]] = (o[LANGS[i]] === undefined ? null : o[LANGS[i]]);
        return c;
      }).filter(function (c) { return c.key; });
      if (!out.cats.length) out.cats = FALLBACK_CATS;
    }
    if (Array.isArray(j.SERVING_OPTIONS) && j.SERVING_OPTIONS.length) out.servings = j.SERVING_OPTIONS;
    /* CHEF_PICK is a single label object, not a list. Normalised the same way the
       categories are — an absent language becomes null so langSpans() renders the
       English fallback with the * gap marker, rather than the string "undefined". */
    if (j.CHEF_PICK && typeof j.CHEF_PICK === 'object') {
      var cp = {};
      for (var q = 0; q < LANGS.length; q++) {
        cp[LANGS[q]] = (j.CHEF_PICK[LANGS[q]] === undefined ? null : j.CHEF_PICK[LANGS[q]]);
      }
      if (cp.en) out.chefLabel = cp;
    }
    return out;
  }
  function scanGaps(tax) {
    var gaps = {};
    [tax.cats, tax.servings].forEach(function (list) {
      (list || []).forEach(function (o) {
        LANGS.forEach(function (l) {
          if (o[l] === null || o[l] === undefined || o[l] === '') gaps[l] = true;
        });
      });
    });
    /* the difficulty chips are server-rendered; their * markers tell us the rest */
    if (document.querySelector('.rh-tag-diff .rh-untranslated[data-l="id"]')) gaps.id = true;
    if (document.querySelector('.rh-tag-diff .rh-untranslated[data-l="fil"]')) gaps.fil = true;
    /* ALLERGEN chips are interface labels too, and they are server-rendered the same way.
       Added 2026-09-10 with the lamb/chef ruling: `lamb`'s null was, by accident, the only
       thing raising this banner, so removing it would have left `mustard`'s * on the
       香草焗羊架 card with nothing on the page explaining what the asterisk means. An
       unexplained * is worse than no *. Scanned from the DOM rather than from a label
       table because that is where the truth is after the server has rendered. */
    if (document.querySelector('.rh-tag-allergen .rh-untranslated[data-l="id"]')) gaps.id = true;
    if (document.querySelector('.rh-tag-allergen .rh-untranslated[data-l="fil"]')) gaps.fil = true;
    return gaps;
  }
  function catByKey(k) {
    for (var i = 0; i < state.cats.length; i++) if (state.cats[i].key === k) return state.cats[i];
    return null;
  }
  /* The categories that have at least one card ON THIS PAGE.

     Two consumers, and they must agree: the filter rail (a pill that always returns
     nothing teaches the reader the filter is broken — the age row has said so since it
     was written) and scanGaps (a "translation missing" banner raised by a label that is
     never rendered is an alarm pointing at nothing).

     Added 2026-09-08 with the `egg` category, the first key to exist in taxonomy.json
     before any recipe uses it. On a corpus where every category is populated this is
     the identity function, which is why the rail got away without it for so long.

     It filters the PILLS, never state.cats: the taxonomy is also what labels a card
     badge, and an empty category must keep its label ready for the first recipe. */
  function catsOnPage(list) {
    var present = {};
    state.recipes.forEach(function (r) { if (r.cat) present[r.cat] = true; });
    return (list || []).filter(function (c) { return present[c.key]; });
  }

  /* ====================================================== DOM slot helpers */
  function slot(article, name) { return article.querySelector('[data-slot="' + name + '"]'); }
  function textOf(el, lang) {
    var s = el && el.querySelector('[data-l="' + lang + '"]');
    return s ? s.textContent.replace(/\*$/, '') : '';
  }

  /* ======================================================== ID/FIL on demand
     Never switch the display language before the text is in the DOM: an empty
     card is worse than a slow one.

     PARTIAL PAYLOAD (the case this section exists for)
     -------------------------------------------------
     A recipe is skipped when the payload has no entry for its SKU, or when the
     entry's ing/step COUNT disagrees with the rendered slots (a merged line would
     shift every following ingredient onto the wrong row — silent corruption, which
     is worse than no translation).

     It used to be skipped and nothing else: the language still switched, so those
     articles had no [data-l="id"] spans at all and the reader got an EMPTY card,
     signalled only by a console.warn. On a 寶寶 recipe that empties the allergen
     banner, in the language of the person doing the feeding.

     So a skipped recipe now gets an explicit FALLBACK: the Chinese text is copied
     into the ID/FIL slots (marked .rh-fallback), the card is flagged
     data-i18n="fallback" and carries a banner saying so in the reader's own
     language, and its 中+ID / 中+FIL share is switched off — sharing a card whose
     "Indonesian" half is Chinese is exactly the silent failure being fixed.

     Chosen over refusing the whole switch: one drifted recipe must not deny the
     other 24 to the helper who needs them, and a marked card is honest where a
     blank one is not. "Looks fine, is blank" is not reachable either way.
  ------------------------------------------------------------------------- */
  function injectI18n(payload) {
    var missing = [], mismatched = [];
    state.recipes.forEach(function (r) {
      /* DECLARED GAP FIRST. A recipe whose card says it is deliberately ZH+EN has no
         payload entry BY DESIGN, so it must not be counted as missing — that would turn
         an expected state into a permanent build-health alarm and bury a real one. */
      if (r.pending.length) { pendingI18n(r); return; }
      var e = payload.recipes ? payload.recipes[r.rid] : null;
      if (!e) { missing.push(r.rid); fallbackI18n(r); return; }
      var art = r.el;
      var ingSlots = art.querySelectorAll('[data-slot^="ing."][data-slot$=".n"]');
      var stepSlots = art.querySelectorAll('[data-slot^="step."]');
      if (e.ing.length !== ingSlots.length || e.steps.length !== stepSlots.length) {
        mismatched.push(r.rid);          /* a merged line is silent corruption — skip it */
        fallbackI18n(r);
        return;
      }
      e.ing.forEach(function (row, i) {
        add(slot(art, 'ing.' + i + '.n'), row[0], row[2], '', null);
        add(slot(art, 'ing.' + i + '.q'), row[1], row[3], '', [row[1], row[3]]);
      });
      e.steps.forEach(function (row, i) {
        add(slot(art, 'step.' + i), row[0], row[1], 'rh-block', null);
      });
      if (e.allergenNote) add(slot(art, 'allergenNote'), e.allergenNote[0], e.allergenNote[1], 'rh-block', null);
      if (e.serveNote) add(slot(art, 'serveNote'), e.serveNote[0], e.serveNote[1], 'rh-block', null);
      if (e.familyShare) add(slot(art, 'familyShare'), e.familyShare[0], e.familyShare[1], 'rh-block', null);
      art.setAttribute('data-i18n', 'ready');
    });

    function add(host, idText, filText, cls, qs) {
      if (!host || host.querySelector('[data-l="id"]')) return;
      CLIENT_LANGS.forEach(function (l, n) {
        var s = document.createElement('span');
        s.setAttribute('data-l', l);
        if (cls) s.className = cls;
        s.textContent = n === 0 ? idText : filText;
        if (qs) s.setAttribute('data-q', qs[n]);
        host.appendChild(s);
      });
    }

    var degraded = missing.concat(mismatched);
    state.i18nDegraded = degraded;
    if (degraded.length) {
      if (window.console && console.warn) {
        console.warn('[recipe-hub] i18n incomplete. missing:', missing,
                     'count mismatch:', mismatched,
                     '— those cards fall back to Chinese and are marked.');
      }
      track('i18n_partial', {
        missing: missing.join(','), mismatched: mismatched.join(','),
        degraded_count: degraded.length, behaviour: 'zh_fallback_marked'
      });
    }
    return !degraded.length;
  }

  /* A DECLARED language gap: fill the ID/FIL slots from the ENGLISH already in the DOM
     and mark every one of them.

     Three rules, and each is a thing that was worse before:
       1. NEVER BLANK. A card with an empty ingredient list in Bahasa is worse than an
          English one — and on a 寶寶 recipe it is the allergen banner that empties, in
          the language of the person doing the feeding.
       2. NEVER SILENTLY. Every injected span carries .rh-untranslated, the * marker,
          the MISSING_TIP tooltip and lang="en", plus a banner on the card. A reader who
          cannot tell our English from our Indonesian cannot trust either.
       3. NEVER HIDE THE CARD. The grid does not change size with the language chosen,
          and a helper who reads Bahasa still needs to find the recipe her employer
          asked for.

     ENGLISH, not Chinese — this is where it differs from fallbackI18n below, which
     copies ZH. For a reader who chose Indonesian or Filipino, English is the more
     legible of the two we have. fallbackI18n keeps ZH because it fires when the payload
     is BROKEN, where the safest thing is the site's primary language.

     It also does NOT report the card as degraded. That is the whole reason this is a
     separate function rather than a flag on the other one. */
  function pendingI18n(r) {
    var art = r.el;
    if (art.getAttribute('data-i18n') === 'pending') return;    /* idempotent */
    var slots = art.querySelectorAll(
      '[data-slot^="ing."], [data-slot^="step."], ' +
      '[data-slot="allergenNote"], [data-slot="serveNote"], [data-slot="familyShare"]');
    for (var i = 0; i < slots.length; i++) {
      var host = slots[i];
      if (host.querySelector('[data-l="id"]')) continue;
      var en = host.querySelector('[data-l="en"]');
      if (!en) continue;
      for (var n = 0; n < r.pending.length; n++) {
        var l = r.pending[n];
        var sp = document.createElement('span');
        sp.setAttribute('data-l', l);
        sp.className = (en.className ? en.className + ' ' : '') + 'rh-untranslated';
        sp.setAttribute('lang', LANG_TAG.en);     /* it IS English — say so to AT */
        sp.setAttribute('title', MISSING_TIP[l] || MISSING_TIP.en);
        sp.textContent = en.textContent;
        if (en.hasAttribute('data-q')) sp.setAttribute('data-q', en.getAttribute('data-q'));
        var star = document.createElement('sup');
        star.setAttribute('aria-hidden', 'true');
        star.textContent = '*';
        sp.appendChild(star);
        host.appendChild(sp);
      }
    }
    var detail = art.querySelector('.rh-detail');
    if (detail && !detail.querySelector('.rh-langmissing')) {
      detail.insertAdjacentHTML('afterbegin',
        '<div class="rh-banner rh-banner-warn rh-langmissing">' +
        tSpans('langPending', 'rh-block') + '</div>');
    }
    art.setAttribute('data-i18n', 'pending');
    /* Same reason as the fallback: sharing a card whose "Indonesian" half is English is
       the silent failure this whole path exists to prevent. ZH+EN still shares. */
    disableClientShare(art);
  }

  /* Fill a skipped recipe's ID/FIL slots with its Chinese text and mark the card.
     Every slot the payload would have written is covered, so no slot is left with
     zero spans for the display language — that is what produced the empty card. */
  function fallbackI18n(r) {
    var art = r.el;
    if (art.getAttribute('data-i18n') === 'fallback') return;   /* idempotent */

    var slots = art.querySelectorAll(
      '[data-slot^="ing."], [data-slot^="step."], ' +
      '[data-slot="allergenNote"], [data-slot="serveNote"]');
    for (var i = 0; i < slots.length; i++) {
      var host = slots[i];
      if (host.querySelector('[data-l="id"]')) continue;
      var zh = host.querySelector('[data-l="zh"]');
      if (!zh) continue;
      for (var n = 0; n < CLIENT_LANGS.length; n++) {
        var s = document.createElement('span');
        s.setAttribute('data-l', CLIENT_LANGS[n]);
        s.className = (zh.className ? zh.className + ' ' : '') + 'rh-fallback';
        s.setAttribute('lang', LANG_TAG.zh);      /* it IS Chinese — say so to AT */
        s.textContent = zh.textContent;
        if (zh.hasAttribute('data-q')) s.setAttribute('data-q', zh.getAttribute('data-q'));
        host.appendChild(s);
      }
    }

    var detail = art.querySelector('.rh-detail');
    if (detail && !detail.querySelector('.rh-langmissing')) {
      detail.insertAdjacentHTML('afterbegin',
        '<div class="rh-banner rh-banner-warn rh-langmissing">' +
        tSpans('langMissing', 'rh-block') + '</div>');
    }
    art.setAttribute('data-i18n', 'fallback');
    disableClientShare(art);
  }

  /* 中+ID / 中+FIL are switched off on a fallback card; 中+EN still works because
     the English text is real. mountDetail() calls this too, for a card opened
     after the payload landed. */
  function disableClientShare(art) {
    var links = art.querySelectorAll('.rh-sharebox [data-share]');
    for (var i = 0; i < links.length; i++) {
      if (CLIENT_LANGS.indexOf(links[i].getAttribute('data-share')) === -1) continue;
      links[i].setAttribute('aria-disabled', 'true');
      links[i].classList.add('is-disabled');
      links[i].removeAttribute('href');
    }
    var box = art.querySelector('.rh-sharebox');
    if (box && links.length && !box.querySelector('.rh-shareblocked')) {
      box.insertAdjacentHTML('beforeend',
        '<p class="rh-shareblocked">' + tSpans('shareBlocked', 'rh-block') + '</p>');
    }
  }

  function ensureI18n() {
    if (state.i18nState === 'ready') return Promise.resolve(true);
    if (state.i18nPromise) return state.i18nPromise;
    var url = CFG.i18nUrl;
    if (!url) return Promise.reject(new Error('no i18nUrl configured'));
    state.i18nState = 'loading';
    showStatus('loading');
    var t0 = (window.performance && performance.now) ? performance.now() : 0;
    state.i18nPromise = getJSON(url).then(function (j) {
      state.i18n = j;
      var clean = injectI18n(j);
      /* 'ready' means "every card has text for the display language", which is now
         true either from the payload or from the marked Chinese fallback. It used to
         be set on a partial payload too, while the skipped cards had nothing at all. */
      state.i18nState = 'ready';
      state.i18nPromise = null;
      updateStatus();
      track('i18n_loaded', {
        bytes_note: 'on demand', complete: clean,
        degraded_count: state.i18nDegraded.length,
        ms: t0 ? Math.round(performance.now() - t0) : null
      });
      return clean;
    }).catch(function (err) {
      state.i18nState = 'failed';
      state.i18nPromise = null;
      showStatus('failed');
      track('i18n_failed', { message: String(err && err.message || err) });
      throw err;
    });
    return state.i18nPromise;
  }

  function showStatus(kind) {
    if (!statusEl) return;
    var warn = (kind === 'failed' || kind === 'partial');
    statusEl.hidden = false;
    statusEl.className = 'rh-banner ' + (warn ? 'rh-banner-warn' : 'rh-banner-scale') + ' rh-status';
    if (kind === 'partial') {
      statusEl.innerHTML = tSpansN('i18nPartial', 'rh-block', state.i18nDegraded.length);
      return;
    }
    statusEl.innerHTML = (kind === 'failed' ? tSpans('i18nFail', 'rh-block') : tSpans('i18nLoading', 'rh-block')) +
      (kind === 'failed' ? ' <button type="button" class="rh-pill" data-i18n-retry>' + tSpans('retry') + '</button>' : '');
  }
  function hideStatus() { if (statusEl) { statusEl.hidden = true; statusEl.innerHTML = ''; } }

  /* The page-level signal for a partial payload. Only shown while an affected
     language is on screen — in ZH/EN nothing is degraded, so there is nothing to
     warn about. Called after every load and every language switch. */
  function updateStatus() {
    if (state.i18nState === 'loading' || state.i18nState === 'failed') return;
    if (state.i18nDegraded.length && CLIENT_LANGS.indexOf(state.lang) !== -1) showStatus('partial');
    else hideStatus();
  }

  /* ================================================================ filters */
  function matches(r) {
    if (state.cat !== 'all' && r.cat !== state.cat) return false;
    if (state.age !== 'all') {
      /* No band = no claim = not shown under an age. See AGE_BANDS above. */
      if (!r.age) return false;
      if (ageMonths(r.age) > ageMonths(state.age)) return false;
    }
    if (state.time !== 'all') {
      var lower = 0, b = null;
      for (var j = 0; j < TIME_BUCKETS.length; j++) {
        if (TIME_BUCKETS[j].key === state.time) { b = TIME_BUCKETS[j]; break; }
        lower = TIME_BUCKETS[j].max;
      }
      if (b && !(r.total > lower && r.total <= b.max)) return false;
    }
    /* Orthogonal to the category above, so it ANDs with it rather than replacing it. */
    if (state.chef && !r.chef) return false;
    for (var k in state.free) {
      if (state.free[k] && r.badges.indexOf(k) !== -1) return false;   /* badges[], never cat */
    }
    if (state.q) {
      var toks = state.q.split(/\s+/).filter(Boolean);
      for (var m = 0; m < toks.length; m++) if (!tokenHit(r.idx, toks[m])) return false;
    }
    return true;
  }
  /* CJK token = substring (no word boundaries, and the index carries bigrams).
     Latin token = word start, so "iga" is Indonesian ribs, not "segitiga". */
  var CJK_RE = /[぀-ヿ㐀-䶿一-鿿豈-﫿]/;
  function tokenHit(idx, tok) {
    if (CJK_RE.test(tok)) return idx.indexOf(tok) !== -1;
    var safe = tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('(^|[^a-z0-9])' + safe).test(idx);
  }

  function refresh() {
    var n = 0;
    state.recipes.forEach(function (r) {
      var show = matches(r);
      r.el.hidden = !show;
      if (show) n++;
    });
    countEl.innerHTML = LANGS.map(function (l) {
      return '<span data-l="' + l + '">' + n + ' ' + esc(n === 1 ? T.resultsOne[l] : T.resultsMany[l]) + '</span>';
    }).join('');
    emptyEl.hidden = n !== 0;
  }

  /* ================================================================= shell
     Structure follows BRAND-GUIDELINES §5.1 (dark navy hero, then content) and
     deliberately does NOT repeat one control shape three times:
       - categories are a horizontally scrolling RAIL of colour-coded tabs
         (§3.5 category accents, which the reference has no equivalent of)
       - time is ONE segmented control
       - diet is a set of checkbox chips
     Every filter button keeps class .rh-pill because the delegated click
     handler is scoped to `.rh-pill[data-cat]` etc. — the fix for the bug where
     the <article>'s own data-cat swallowed card clicks. Do not rename the hook.
  ------------------------------------------------------------------------- */
  function langBtnsHtml() {
    return LANGS.map(function (l) {
      return '<button type="button" class="rh-lang" data-lang-btn="' + l + '" lang="' + LANG_TAG[l] + '" ' +
             'aria-pressed="' + (l === state.lang) + '">' + LANG_LABEL[l] + '</button>';
    }).join('');
  }

  /* The <header> is SERVER-RENDERED (build_page.py -> snippets/recipe-hero.liquid),
     so the page has its <h1> with JavaScript off. Exactly ONE thing is mounted into
     it here — the language buttons, which a JS-off reader has no use for.
     Deliberately NOT rebuilt on the taxonomy refresh: it is content, not chrome. */
  function mountHead() {
    headEl = root.querySelector('.rh-head');
    if (!headEl) {
      /* The hero snippet is missing — an un-uploaded or stale theme. Emit just
         enough for the switcher to work rather than duplicating the hero copy in
         two files; the h1 is build_page.py's to own. */
      if (window.console && console.warn) {
        console.warn('[recipe-hub] no .rh-head in the page — render snippets/recipe-hero.liquid ' +
                     '(build_page.py) before recipe-cards-*.liquid.');
      }
      root.insertAdjacentHTML('afterbegin',
        '<header class="rh-head">' +
        '<div class="rh-hero"><div class="rh-hero-in">' +
        '<div class="rh-langs" role="group" aria-label="Language"></div>' +
        '</div></div></header>');
      headEl = root.querySelector('.rh-head');
    }
    var langs = headEl.querySelector('.rh-langs');
    if (langs) langs.innerHTML = langBtnsHtml();
  }

  function shellHtml() {
    /* ONLY THE CATEGORIES ACTUALLY ON THE PAGE — see catsOnPage(). Same rule the age
       row below already had: a pill that always returns nothing teaches the reader the
       filter is broken. */
    var catTabs = '<button type="button" class="rh-pill" data-cat="all" aria-pressed="true">' + tSpans('all') + '</button>' +
      catsOnPage(state.cats).map(function (c) {
        return '<button type="button" class="rh-pill" data-cat="' + esc(c.key) + '" aria-pressed="false">' + langSpans(c) + '</button>';
      }).join('');
    var timePills = '<button type="button" class="rh-pill" data-time="all" aria-pressed="true">' + tSpans('all') + '</button>' +
      TIME_BUCKETS.map(function (b) {
        return '<button type="button" class="rh-pill" data-time="' + b.key + '" aria-pressed="false">' + langSpans(b) + '</button>';
      }).join('');
    /* Only the bands actually on the page, in ascending order. A pill that always
       returns nothing teaches the reader the filter is broken. */
    var present = {};
    state.recipes.forEach(function (r) { if (r.age) present[r.age] = true; });
    var ageRow = '';
    if (state.hasAges) {
      var agePills = '<button type="button" class="rh-pill" data-age="all" aria-pressed="true">' + tSpans('all') + '</button>' +
        AGE_BANDS.filter(function (b) { return present[b]; }).map(function (b) {
          return '<button type="button" class="rh-pill" data-age="' + b + '" aria-pressed="false">' + langSpans(ageLabel(b)) + '</button>';
        }).join('');
      ageRow = '<div class="rh-row"><span class="rh-rowlabel">' + tSpans('ageRow') + '</span>' +
        '<span class="rh-seg" role="group">' + agePills + '</span></div>';
    }
    var dietPills = Object.keys(FREE_LABEL).map(function (k) {
      return '<button type="button" class="rh-pill rh-diet" data-free="' + k + '" aria-pressed="false">' + langSpans(FREE_LABEL[k]) + '</button>';
    }).join('');
    /* Rendered only when the page actually carries a chef-inspired card — the same rule
       the category and age rows follow: a pill that always returns nothing teaches the
       reader the filter is broken.

       IT SITS IN THE RAIL but is NOT a category: aria-pressed toggles independently, it
       carries no data-cat, and .rh-chef-sep puts a hairline before it so the eye reads
       "categories, then a separate tag" — which is what Kelvin asked for. The rail's
       aria-label stays "Category", so the pill names itself for a screen reader via
       aria-label rather than borrowing the group's. */
    var chefPill = '';
    if (state.hasChef) {
      var cl = state.chefLabel || FALLBACK_CHEF;
      chefPill = '<span class="rh-chef-sep" aria-hidden="true"></span>' +
        '<button type="button" class="rh-pill rh-chef" data-chef="1" aria-pressed="false"' +
        ' aria-label="' + esc(cl.en) + '">' +
        '<span class="rh-chef-i" aria-hidden="true">\uD83E\uDDD1\u200D\uD83C\uDF73</span>' +
        langSpans(cl) + '</button>';
    }
    return '' +
      '<div class="rh-controls">' +
        '<nav class="rh-rail" aria-label="Category">' +
          '<span class="rh-sr">' + tSpans('catRow') + '</span>' + catTabs + chefPill +
        '</nav>' +
        '<div class="rh-tools">' +
          '<div class="rh-searchwrap">' +
            '<label class="rh-sr" for="rh-search">Search</label>' +
            '<input id="rh-search" class="rh-search" type="search" autocomplete="off" />' +
            '<button type="button" class="rh-clear" aria-label="clear">&times;</button>' +
          '</div>' +
          '<div class="rh-filters">' +
            '<div class="rh-row"><span class="rh-rowlabel">' + tSpans('timeRow') + '</span>' +
              '<span class="rh-seg" role="group">' + timePills + '</span></div>' +
            ageRow +
            '<div class="rh-row"><span class="rh-rowlabel">' + tSpans('dietRow') + '</span>' +
              '<span class="rh-diets" role="group">' + dietPills + '</span></div>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="rh-banner rh-banner-scale rh-gap" hidden>' + tSpans('gap', 'rh-block') + '</div>' +
      '<div class="rh-status" hidden></div>' +
      '<p class="rh-count" aria-live="polite"></p>';
  }

  /* ---------------------------------------------------- interactive mounts */
  function mountDetail(r) {
    var art = r.el;
    if (art.getAttribute('data-mounted')) return;
    art.setAttribute('data-mounted', '1');

    var scaler = art.querySelector('.rh-scaler-mount');
    if (scaler) {
      scaler.innerHTML =
        '<div class="rh-scaler"><span class="rh-scaler-label">' + tSpans('servings') + '</span>' +
        '<span class="rh-mults" role="group">' +
        state.servings.map(function (o) {
          return '<button type="button" class="rh-pill rh-mult" data-mult="' + o.mult + '" aria-pressed="' +
            (o.mult === 1) + '">&times;' + o.mult + ' ' + langSpans(o) + '</button>';
        }).join('') + '</span>' +
        '<span class="rh-serves" aria-live="polite">' + LANGS.map(function (l) {
          return '<span data-l="' + l + '">' + esc(servesText(r.serves, l, isBabyMeal(r), isPieces(r))) + '</span>';
        }).join('') + '</span></div>' +
        '<div class="rh-banner rh-banner-scale rh-scalenote" hidden>' + tSpans('scaleNote', 'rh-block') + '</div>' +
        (r.linear ? '' :
          '<div class="rh-banner rh-banner-warn rh-scalewarn" hidden>' +
          langSpans(scaleWarnFor(r.rid), 'rh-block') + '</div>');
    }
    /* THE ORDERING-LINK GATE, at the point of sharing.
       A card may share when it has a branded /wa/ link, OR when it declares that it
       has no product at all (data-no-product, ruling 2) — in which case the shared
       body simply carries no 🛒 line. A card with neither is the stale-snippet case:
       both builds assert the link for every product-backed recipe, so an empty link
       with no declaration means the theme is carrying output older than its data, and
       the share is withheld rather than sent linkless.

       The declaration is what separates the two. Not the empty string: every way of
       LOSING a link also produces an empty string. */
    var canShare = !!(r.orderLink || r.noProduct);
    var shareMount = art.querySelector('.rh-share-mount');
    if (shareMount && canShare) {
      shareMount.innerHTML = '<button type="button" class="rh-share" data-share-toggle>💬 ' + tSpans('share') + '</button>';
    } else if (shareMount) {
      if (window.console && console.warn) {
        console.warn('[recipe-hub] no data-order-link and no data-no-product on ' +
                     r.rid + ' — share withheld. Re-run build_data.py + build_page.py ' +
                     'and re-upload the snippets.');
      }
      track('share_withheld', { recipe_sku: r.sku, recipe_id: r.rid, reason: 'no_order_link' });
    }
    var box = art.querySelector('.rh-sharebox');
    if (box && canShare) {
      box.innerHTML = '<p>' + tSpans('sharePick') + '</p><div class="rh-sharepick">' +
        ['en', 'id', 'fil'].map(function (l) {
          return '<a href="#" data-share="' + l + '" target="_blank" rel="noopener">中 + ' + LANG_LABEL[l] + '</a>';
        }).join('') + '</div>';
      /* the card may already have been flagged by a payload that landed before it
         was opened — re-apply, since the links were only just created */
      if (art.getAttribute('data-i18n') === 'fallback') disableClientShare(art);
    }
    var closeMount = art.querySelector('.rh-close-mount');
    if (closeMount) closeMount.innerHTML = '<button type="button" class="rh-close">' + tSpans('close') + '</button>';
  }

  function applyScale(r) {
    var art = r.el;
    var f = state.scale[r.rid] || 1;
    var qs = art.querySelectorAll('.rh-ing-q [data-q]');
    for (var i = 0; i < qs.length; i++) {
      var l = qs[i].getAttribute('data-l');
      qs[i].textContent = scaleQty(qs[i].getAttribute('data-q'), f, l);
      qs[i].parentNode.classList.toggle('is-scaled', f !== 1);
    }
    var serves = art.querySelectorAll('.rh-serves [data-l]');
    for (var j = 0; j < serves.length; j++) {
      serves[j].textContent = servesText(r.serves * f, serves[j].getAttribute('data-l'), isBabyMeal(r), isPieces(r));
    }
    var note = art.querySelector('.rh-scalenote');
    if (note) note.hidden = (f === 1);
    var warn = art.querySelector('.rh-scalewarn');
    if (warn) warn.hidden = (f === 1);            /* data-linear="0" is why it exists at all */
    var pills = art.querySelectorAll('[data-mult]');
    for (var k = 0; k < pills.length; k++) {
      pills[k].setAttribute('aria-pressed', String(parseInt(pills[k].getAttribute('data-mult'), 10) === f));
    }
  }

  /* =============================================================== share
     Built from the DOM, so it uses exactly the text the reader is looking at
     (and the scaled quantities). ID/FIL are loaded first if needed.
  ------------------------------------------------------------------------- */
  function shareText(r, other) {
    var art = r.el;
    var f = state.scale[r.rid] || 1;
    var lines = [];
    lines.push('🍳 ' + textOf(slot(art, 'name'), 'zh') + ' / ' + textOf(slot(art, 'name'), other));
    /* 👥 is two PEOPLE. On a baby card the count is meals for one baby, so the
       icon changes with the unit -- the helper cooks from this message. */
    lines.push((isBabyMeal(r) ? '\uD83C\uDF7C ' : '\uD83D\uDC65 ') +
      servesText(r.serves * f, 'zh', isBabyMeal(r), isPieces(r)) + ' / ' + servesText(r.serves * f, other, isBabyMeal(r), isPieces(r)));
    lines.push('');
    lines.push('🧂 ' + textOf(slot(art, 'lbl.ingredients'), 'zh') + ' / ' + textOf(slot(art, 'lbl.ingredients'), other));
    var n = art.querySelectorAll('[data-slot^="ing."][data-slot$=".n"]').length;
    for (var i = 0; i < n; i++) {
      var ns = slot(art, 'ing.' + i + '.n'), qsl = slot(art, 'ing.' + i + '.q');
      ['zh', other].forEach(function (l) {
        var q = qsl && qsl.querySelector('[data-l="' + l + '"]');
        lines.push('• ' + textOf(ns, l) + ' ' + (q ? scaleQty(q.getAttribute('data-q'), f, l) : ''));
      });
    }
    lines.push('');
    lines.push('🔥 ' + textOf(slot(art, 'lbl.steps'), 'zh') + ' / ' + textOf(slot(art, 'lbl.steps'), other));
    var steps = art.querySelectorAll('[data-slot^="step."]');
    for (var s = 0; s < steps.length; s++) {
      lines.push((s + 1) + '. ' + textOf(steps[s], 'zh'));
      lines.push('   ' + textOf(steps[s], other));
    }
    var an = slot(art, 'allergenNote');
    if (an) { lines.push(''); lines.push(textOf(an, 'zh')); lines.push(textOf(an, other)); }
    var sn = slot(art, 'serveNote');
    if (sn) { lines.push(''); lines.push(textOf(sn, 'zh')); lines.push(textOf(sn, other)); }
    /* 一盤兩食. THE REASON THIS FIELD EXISTS AT ALL is that it has to be in here:
       the helper cooks from this message, not from the blog post the card links
       to, so a family-share section left on the blog is a section she never sees.
       Placed after the serving note and before the ordering link, i.e. last thing
       before the reader is asked to buy — it is the "you can feed everyone from
       this pot" line, and it is where a cook is still reading. */
    var fs = slot(art, 'familyShare');
    if (fs) { lines.push(''); lines.push(textOf(fs, 'zh')); lines.push(textOf(fs, other)); }
    if (!r.linear && f > 1) {
      var w = scaleWarnFor(r.rid);
      lines.push(''); lines.push(w.zh); lines.push(w[other] || w.en);
    }
    /* CREDIT — whose dish this is. IN the message, and this is the familyShare side of
       the argument, not the blogUrl side: an attribution is part of the recipe, and a
       recipe pasted into WhatsApp without it is the recipe with the credit stripped
       off. The helper is also the person most likely to pass it on again.

       ONLY THE TEXT. The credit URL is NOT here, and that IS the blogUrl rule: the
       share already carries 📖 the hub and 🛒 the ordering link, and a third
       destination points her out of the message she is cooking from. Nothing to
       suppress, because this is built from the DOM's text nodes — an href is not text.
       Asserted anyway in test_share_body.js, checks 18-21, because absence is
       invisible until someone adds it.

       LAST TEXT BLOCK, after the scale warning and before the links: the scale warning
       is operational (it is about the quantities she is about to use) and this is not,
       so attribution sits where attribution sits — at the foot, above the links. */
    var cr = slot(art, 'credit');
    if (cr) { lines.push(''); lines.push(textOf(cr, 'zh')); lines.push(textOf(cr, other)); }
    lines.push('');
    /* ORDERING-LINK GATE (CLAUDE.md, PLAN §5). The shared message is customer-facing
       product copy, so it carries the BRANDED ordering link — https://slibuddies.net/wa/<slug>
       — not the /products/<handle> storefront URL it used to carry. A product-page URL
       does not satisfy the gate, the same way a collection link does not.

       The value is looked up at BUILD time (build_data.py, from content-database.csv /
       wa-links.json) and arrives on the card as data-order-link. Never construct it here:
       a /wa/ slug only resolves if a real Shopify redirect backs it.

       wa.me/?text= stays as the share TRANSPORT — it carries no phone number, so it
       means "share to whoever you pick", which is the employer -> helper handoff. The
       body was the problem, not the mechanism. */
    /* A recipe that declares noProduct has nothing to sell, so the 🛒 line is OMITTED
       — not left blank, not pointed at a collection, not pointed at a plausible
       ingredient. The 📖 link to the hub is always there, so the message is never
       link-less. hydrate() and mountDetail() have already refused to arm this share
       for a card that has neither a link nor the declaration. */
    if (r.orderLink) lines.push('🛒 ' + r.orderLink);
    lines.push('📖 ' + PAGE_URL);
    return lines.join('\n');
  }

  /* ================================================================ switch */
  function setLang(l, opts) {
    if (LANGS.indexOf(l) === -1) return Promise.resolve(false);
    var needsFetch = CLIENT_LANGS.indexOf(l) !== -1 && state.i18nState !== 'ready';
    if (needsFetch) {
      /* stay on the current language until the text exists — never an empty card */
      return ensureI18n().then(function () { return applyLang(l, opts); })
        .catch(function () { return false; });
    }
    return Promise.resolve(applyLang(l, opts));
  }
  function applyLang(l, opts) {
    state.lang = l;
    root.setAttribute('data-lang', l);
    root.setAttribute('lang', LANG_TAG[l]);
    var btns = root.querySelectorAll('[data-lang-btn]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-pressed', String(btns[i].getAttribute('data-lang-btn') === l));
    }
    var input = root.querySelector('.rh-search');
    if (input) input.placeholder = T.search[l];
    var gap = root.querySelector('.rh-gap');
    if (gap) gap.hidden = !state.gapLangs[l];
    updateStatus();
    try { localStorage.setItem(STORE_KEY, l); } catch (e) {}
    if (!(opts && opts.silent)) track('lang_switch', { lang: l, source: (opts && opts.source) || 'click' });
    return true;
  }
  function pressGroup(sel, attr, val) {
    var btns = root.querySelectorAll(sel);
    for (var i = 0; i < btns.length; i++) btns[i].setAttribute('aria-pressed', String(btns[i].getAttribute(attr) === val));
  }

  /* ================================================================= boot */
  function hydrate() {
    var cards = gridEl.querySelectorAll('.rh-card');
    state.recipes = Array.prototype.map.call(cards, function (el) {
      var badges = (el.getAttribute('data-badges') || '').split(',').filter(Boolean);
      var r = {
        el: el,
        /* IDENTITY. data-rid, on EVERY card, and it is the recipe `id`. Used for
           the scaler state, the i18n lookup and the analytics recipe_id.

           The `|| data-sku` fallback was DELETED on 2026-08-31, not left in place
           unreachable: a SKU identifies a product and several recipes share one, so
           reading it as identity would file every recipe of a product in one scaler
           and i18n bucket. A fallback that can no longer fire is also a false
           statement about the data — the next reader would infer that data-sku is
           still sometimes the identity. It is not, anywhere. */
        rid: el.getAttribute('data-rid') || '',
        /* PRODUCT REFERENCE, never identity. null when the recipe has none — never
           '' pretending to be one. Only ever reported as analytics recipe_sku. */
        sku: el.getAttribute('data-sku'),
        /* The positive declaration from the data (ruling 2). This — not an empty
           orderLink — is what makes a missing buy link legitimate. */
        noProduct: el.getAttribute('data-no-product') || '',
        /* DECLARED language gap, from build_data.py's pendingLangs. Read as a TOKEN,
           never inferred from the payload not having an entry: a missing entry is also
           what a broken build looks like, and the two must not share a signal. */
        pending: (el.getAttribute('data-pending') || '').split(',').filter(Boolean),
        cat: el.getAttribute('data-cat'),
        /* Presence of the token, never a credit string sniffed out of the card:
           build_page.py asserts the token, the head chip and the credit line all
           agree, so this is the one declaration worth reading. */
        chef: el.getAttribute('data-chef') === '1',
        age: el.getAttribute('data-age') || '',
        handle: el.getAttribute('data-handle') || '',
        /* branded /wa/ ordering link, resolved by build_data.py. Both builds refuse
           to emit a product-backed card without one, so an empty value on a card
           that does NOT declare noProduct means a stale snippet — the share is
           withheld rather than sent linkless (see mountDetail). */
        orderLink: el.getAttribute('data-order-link') || '',
        badges: badges,
        total: parseInt(el.getAttribute('data-total'), 10) || 0,
        serves: parseInt(el.getAttribute('data-serves'), 10) || 1,
        linear: el.getAttribute('data-linear') !== '0',
        idx: norm(el.getAttribute('data-idx') || '')
      };
      el._r = r;
      return r;
    });
    state.hasAges = state.recipes.some(function (r) { return !!r.age; });
    state.hasChef = state.recipes.some(function (r) { return r.chef; });
  }

  /* Delegated from #recipe-hub, which survives the one shell rebuild. */
  function bind() {
    root.addEventListener('click', function (ev) {
      var t = ev.target;
      /* Every filter matcher is scoped to .rh-pill on purpose: the <article>
         itself carries data-cat / data-diff for filtering, so a bare
         t.closest('[data-cat]') matched the CARD and swallowed every click
         inside it. Found in the browser, not in review. */

      if (t.closest('[data-i18n-retry]')) { ensureI18n().catch(function () {}); return; }

      var langBtn = t.closest('[data-lang-btn]');
      if (langBtn) { setLang(langBtn.getAttribute('data-lang-btn')); return; }

      var catBtn = t.closest('.rh-pill[data-cat]');
      if (catBtn) {
        state.cat = catBtn.getAttribute('data-cat');
        pressGroup('.rh-pill[data-cat]', 'data-cat', state.cat);
        refresh(); track('filter', { kind: 'category', value: state.cat, lang: state.lang }); return;
      }
      /* Toggle, not a group: there is one of these and it flips. Scoped to .rh-pill for
         the same reason data-cat is — the CARD carries data-chef too, and an unscoped
         closest() would swallow every click on a chef-inspired card. */
      var chefBtn = t.closest('.rh-pill[data-chef]');
      if (chefBtn) {
        state.chef = !state.chef;
        chefBtn.setAttribute('aria-pressed', String(state.chef));
        refresh(); track('filter', { kind: 'chef', on: state.chef, lang: state.lang }); return;
      }
      var timeBtn = t.closest('.rh-pill[data-time]');
      if (timeBtn) {
        state.time = timeBtn.getAttribute('data-time');
        pressGroup('.rh-pill[data-time]', 'data-time', state.time);
        refresh(); track('filter', { kind: 'time', value: state.time, lang: state.lang }); return;
      }
      var ageBtn = t.closest('.rh-pill[data-age]');
      if (ageBtn) {
        state.age = ageBtn.getAttribute('data-age');
        pressGroup('.rh-pill[data-age]', 'data-age', state.age);
        refresh(); track('filter', { kind: 'age', value: state.age, lang: state.lang }); return;
      }
      var freeBtn = t.closest('.rh-pill[data-free]');
      if (freeBtn) {
        var fk = freeBtn.getAttribute('data-free');
        state.free[fk] = !state.free[fk];
        freeBtn.setAttribute('aria-pressed', String(!!state.free[fk]));
        refresh(); track('filter', { kind: 'diet', value: 'no_' + fk, on: !!state.free[fk], lang: state.lang }); return;
      }

      var buy = t.closest('[data-buy]');
      if (buy) {
        var art0 = buy.closest('.rh-card');
        track('buy_click', {
          recipe_sku: art0 ? art0.getAttribute('data-sku') : null,
          recipe_id: art0 && art0._r ? art0._r.rid : null,
          product_handle: buy.getAttribute('data-buy'),
          kind: buy.getAttribute('data-buy-kind'),
          lang: state.lang
        });
        return;
      }

      var article = t.closest('.rh-card');
      if (!article) return;
      var r = article._r;

      var cardBtn = t.closest('.rh-cardbtn');
      if (cardBtn) {
        var open = article.classList.toggle('is-open');
        cardBtn.setAttribute('aria-expanded', String(open));
        if (open) { mountDetail(r); applyScale(r); track('open', { recipe_sku: r.sku, recipe_id: r.rid, lang: state.lang }); }
        return;
      }
      if (t.closest('.rh-close')) {
        article.classList.remove('is-open');
        var b = article.querySelector('.rh-cardbtn');
        b.setAttribute('aria-expanded', 'false'); b.focus();
        return;
      }

      var sc = t.closest('.rh-pill[data-mult]');
      if (sc) {
        var next = parseInt(sc.getAttribute('data-mult'), 10) || 1;
        state.scale[r.rid] = next;
        applyScale(r);
        track('scale', {
          recipe_sku: r.sku, recipe_id: r.rid, base_serves: r.serves, multiplier: next,
          target_serves: r.serves * next, linear: r.linear, lang: state.lang
        });
        return;
      }

      if (t.closest('[data-share-toggle]')) {
        var box = article.querySelector('.rh-sharebox');
        box.hidden = !box.hidden;
        /* Opening the picker is the reader signalling intent, so warm the ID/FIL
           payload now. It lands while they read the two lines of picker text, and
           the 中+ID / 中+FIL tap is then an ordinary link click rather than a tap
           that appears to do nothing. */
        if (!box.hidden && state.i18nState !== 'ready') ensureI18n().catch(function () {});
        return;
      }

      var sh = t.closest('[data-share]');
      if (sh) {
        var other = sh.getAttribute('data-share');

        /* Authoritative guards. disableClientShare() also strips the href and marks
           the link, but a click must never fall through to a share that is half
           Chinese, or to one with no ordering link in it. */
        if (!r.orderLink && !r.noProduct) {
          ev.preventDefault();
          track('share_withheld', { recipe_sku: r.sku, recipe_id: r.rid, pair: 'zh+' + other, reason: 'no_order_link' });
          return;
        }
        if (CLIENT_LANGS.indexOf(other) !== -1 && article.getAttribute('data-i18n') === 'fallback') {
          ev.preventDefault();
          track('share_withheld', { recipe_sku: r.sku, recipe_id: r.rid, pair: 'zh+' + other, reason: 'i18n_fallback' });
          return;
        }

        if (CLIENT_LANGS.indexOf(other) !== -1 && state.i18nState !== 'ready') {
          /* Fallback for a slow network: never open WhatsApp with half a recipe.
             The status banner says what is happening; the href is armed as soon
             as the text lands, so the second tap shares the full thing. */
          ev.preventDefault();
          track('share_deferred', { recipe_sku: r.sku, recipe_id: r.rid, pair: 'zh+' + other, lang: state.lang });
          ensureI18n().then(function () {
            /* the payload may have landed WITHOUT this recipe — arming the href then
               would hand over a share whose "Indonesian" half is Chinese */
            if (article.getAttribute('data-i18n') === 'fallback') { disableClientShare(article); return; }
            sh.href = 'https://wa.me/?text=' + encodeURIComponent(shareText(r, other));
          }).catch(function () {});
          return;
        }
        sh.href = 'https://wa.me/?text=' + encodeURIComponent(shareText(r, other));
        track('share', {
          recipe_sku: r.sku, recipe_id: r.rid, pair: 'zh+' + other, lang: state.lang,
          multiplier: state.scale[r.rid] || 1, target_serves: r.serves * (state.scale[r.rid] || 1)
        });
      }
    });

    root.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape') return;
      var open = root.querySelector('.rh-card.is-open');
      if (open) {
        open.classList.remove('is-open');
        var b = open.querySelector('.rh-cardbtn');
        b.setAttribute('aria-expanded', 'false'); b.focus();
      }
    });
  }

  function start() {
    root = document.getElementById('recipe-hub');
    if (!root) return;
    root.classList.add('rh');
    gridEl = root.querySelector('.rh-grid');
    if (!gridEl || !gridEl.querySelector('.rh-card')) {
      root.innerHTML = '<div class="rh-empty"><p><strong>Recipe cards are missing.</strong></p>' +
        '<p>The page expects the pre-rendered cards from <code>build_page.py</code> ' +
        '(theme snippets <code>recipe-cards-*.liquid</code>, or <code>data/prerendered.html</code> in preview).</p></div>';
      return;
    }

    var initial = CFG.lang || 'zh';
    try { initial = localStorage.getItem(STORE_KEY) || initial; } catch (e) {}
    var m = /[?&]lang=(zh|en|id|fil)\b/.exec(window.location.search);
    if (m) initial = m[1];
    if (LANGS.indexOf(initial) === -1) initial = 'zh';

    hydrate();
    mountHead();
    headEl.insertAdjacentHTML('afterend', shellHtml());
    gridEl.insertAdjacentHTML('afterend',
      '<div class="rh-empty" hidden>' + tSpans('empty') + '</div>' +
      '<p class="rh-foot">' + tSpans('footer') + '</p>');
    countEl = root.querySelector('.rh-count');
    emptyEl = root.querySelector('.rh-empty');
    statusEl = root.querySelector('.rh-status');

    bind();
    bindShell();
    applyLang(SERVER_LANGS.indexOf(initial) !== -1 ? initial : 'zh', { silent: true });
    refresh();
    track('view', { recipes: state.recipes.length, lang: state.lang, prerendered: SERVER_LANGS.join(',') });

    /* taxonomy is 2KB and only feeds the filter pills + scaler labels */
    if (CFG.taxonomyUrl) {
      getJSON(CFG.taxonomyUrl).then(function (j) {
        var tax = coerceTaxonomy(j);
        state.cats = tax.cats; state.servings = tax.servings;
        state.chefLabel = tax.chefLabel || FALLBACK_CHEF;
        /* catsOnPage, not tax.cats: a null label on a category with no cards raises a
           "translation missing" banner for a pill that is not rendered. Same filter as
           the rail, so the banner and the page agree. */
        state.gapLangs = scanGaps({ cats: catsOnPage(tax.cats), servings: tax.servings });
        /* Rebuild only the pill rows — not the cards, and NOT the header: the hero
           is server-rendered content and the language buttons inside it are already
           bound and pressed correctly. */
        var ctl = root.querySelector('.rh-controls'),
            gap = root.querySelector('.rh-gap'), st = root.querySelector('.rh-status'),
            cnt = root.querySelector('.rh-count');
        [ctl, gap, st, cnt].forEach(function (el) { if (el) el.parentNode.removeChild(el); });
        headEl.insertAdjacentHTML('afterend', shellHtml());
        countEl = root.querySelector('.rh-count');
        statusEl = root.querySelector('.rh-status');
        bindShell();
        applyLang(state.lang, { silent: true });
        refresh();
        if (initial !== state.lang) setLang(initial, { source: 'restored' });
      }).catch(function () {
        state.gapLangs = scanGaps({ cats: catsOnPage(state.cats), servings: state.servings });
        if (initial !== state.lang) setLang(initial, { source: 'restored' });
      });
    } else if (initial !== state.lang) {
      setLang(initial, { source: 'restored' });
    }
  }

  /* the shell is replaced once, after taxonomy lands; only its own listeners
     need re-binding (the click handler is delegated from root, which survives) */
  function bindShell() {
    var input = root.querySelector('.rh-search');
    var clearBtn = root.querySelector('.rh-clear');
    var searchTimer = null;
    input.addEventListener('input', function () {
      state.q = norm(input.value.trim());
      clearBtn.classList.toggle('is-on', input.value.length > 0);
      refresh();
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        if (state.q) track('search', { query: state.q, lang: state.lang, results: gridEl.querySelectorAll('.rh-card:not([hidden])').length });
      }, 700);
    });
    clearBtn.addEventListener('click', function () {
      input.value = ''; state.q = ''; clearBtn.classList.remove('is-on'); refresh(); input.focus();
    });
  }

  if (!Element.prototype.closest) {
    Element.prototype.closest = function (s) {
      var el = this;
      while (el && el.nodeType === 1) { if (el.matches(s)) return el; el = el.parentElement; }
      return null;
    };
  }

  window.RecipeHub = {
    scaleQty: scaleQty, norm: norm, shareText: shareText, setLang: setLang,
    ensureI18n: ensureI18n, state: state, version: VERSION,
    /* exported so the age filter can be tested without a browser: it is a safety
       filter, and "showed a 12m recipe under 8m" is not something to find out on a
       phone. See _Tools/recipe-hub-deploy/test_age_filter.js */
    matches: matches, ageMonths: ageMonths, AGE_BANDS: AGE_BANDS
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
